/**
 * Columns: left → right is cause and time.
 *
 * A forward-typed edge (leads_to, enables, blocks, delays) "A → B" says B
 * comes after A, so B's column is right of A's — across branches and lanes
 * too. A card's column is the longest path to it over those edges and the
 * tree it hangs from, with its kind's first column as a floor (input, output,
 * outcome, impact start where they start in every lane). Feedback types
 * (reinforces, weakens) do not order columns: drawn pointing back, they are
 * what makes a loop visible. A cycle of forward edges is broken at the loop's
 * closing edge when the graph names one, else at the edge whose source sits
 * deepest; the broken edges are returned. Columns no card uses are removed.
 */
const KIND_RANK = { input: 0, output: 1, outcome: 2, impact: 3 };
export const FORWARD_TYPES = new Set(['leads_to', 'enables', 'blocks', 'delays']);

/** The tree-only column key: kind rank first, steps within the kind second. */
function kindKeys(lanes, tree, nodeById) {
  const key = new Map();
  const walk = (id, depth, rank, parentIsStart) => {
    const n = nodeById.get(id);
    const own = KIND_RANK[n.attrs.kind] ?? KIND_RANK[n.attrs.chainKind];
    const r = own ?? rank;
    const d = (!parentIsStart && r === rank) ? depth + 1 : 0;
    key.set(id, { rank: own ?? null, k: r * 1000 + d });
    (tree.children.get(id) || []).forEach((k) => walk(k, d, r, false));
  };
  lanes.forEach((lane) => (tree.topsByLane.get(lane.id) || []).forEach((t) => walk(t, 0, 0, true)));
  return key;
}

/**
 * @param edges         kept graph edges
 * @param placedOn      Map blocker id → { host: target id, offset } (column = host's − offset)
 * @param loopClosers   Set of "from->to" pairs that close a named loop
 * @returns { column: Map id → column, broken: [{ id, reason: 'forward_cycle' }] }
 */
export function assignColumns({ lanes, tree, nodeById, blockersOn, edges, placedOn, loopClosers, isPinned }) {
  const key = kindKeys(lanes, tree, nodeById);
  const members = [...key.keys()];
  const ordered = [...new Set([...key.values()].map((v) => v.k))].sort((a, b) => a - b);
  const colOfKey = new Map(ordered.map((k, i) => [k, i + 1]));
  // A kind's floor: the first column it has anywhere.
  const floorOfRank = new Map();
  key.forEach((v) => {
    if (v.rank === null) return;
    const c = colOfKey.get(v.k);
    floorOfRank.set(v.rank, Math.min(floorOfRank.get(v.rank) ?? c, c));
  });
  const floor = (id) => {
    const v = key.get(id);
    return v.rank === null ? 1 : floorOfRank.get(v.rank);
  };

  // Constraints u → v with weight w: column(v) ≥ column(u) + w. A placed
  // blocker is represented by the card it stands in front of.
  const at = (id) => (placedOn.has(id) ? { id: placedOn.get(id).host, shift: -placedOn.get(id).offset } : (key.has(id) ? { id, shift: 0 } : null));
  const cons = [];
  members.forEach((id) => {
    const p = tree.parentOf.get(id);
    if (p && key.has(p)) cons.push({ u: p, v: id, w: 1 + blockersOn(p, id).length, tree: true, id: `tree:${id}`, order: -1 });
  });
  edges.forEach((e, order) => {
    if (!FORWARD_TYPES.has(e.attrs.type) || isPinned(e.attrs.from) || isPinned(e.attrs.to)) return;
    const a = at(e.attrs.from);
    const b = at(e.attrs.to);
    if (!a || !b || a.id === b.id) return;
    if (tree.parentOf.get(e.attrs.to) === e.attrs.from && !placedOn.has(e.attrs.to)) return; // already the tree constraint
    cons.push({ u: a.id, v: b.id, w: 1 + a.shift - b.shift, edge: e, id: e.id, order });
  });

  // Break forward cycles: named loop closers first, then the edge out of the deepest source.
  const broken = [];
  const depth = (id) => colOfKey.get(key.get(id).k);
  const active = new Set(cons.map((c, i) => i));
  for (;;) {
    const cyc = findCycle(members, cons, active);
    if (!cyc) break;
    const candidates = cyc.filter((i) => !cons[i].tree);
    if (!candidates.length) break; // the tree itself has no cycles; defensive
    const closer = candidates.find((i) => loopClosers.has(`${cons[i].edge.attrs.from}->${cons[i].edge.attrs.to}`));
    const pick = closer ?? candidates.reduce((best, i) => {
      const d = depth(cons[i].u);
      const bd = depth(cons[best].u);
      return d > bd || (d === bd && cons[i].order < cons[best].order) ? i : best;
    });
    active.delete(pick);
    broken.push({ id: cons[pick].id, reason: 'forward_cycle' });
  }

  // Longest path over what remains (a DAG now), floors included.
  const column = new Map(members.map((id) => [id, floor(id)]));
  const outs = new Map();
  const indeg = new Map(members.map((id) => [id, 0]));
  active.forEach((i) => {
    const c = cons[i];
    if (!outs.has(c.u)) outs.set(c.u, []);
    outs.get(c.u).push(c);
    indeg.set(c.v, indeg.get(c.v) + 1);
  });
  // Tops hang from the start (column 0), plus the blockers on that edge.
  members.forEach((id) => {
    if (!key.has(tree.parentOf.get(id))) column.set(id, Math.max(column.get(id), 1 + blockersOn(tree.parentOf.get(id), id).length));
  });
  const queue = members.filter((id) => indeg.get(id) === 0);
  for (let qi = 0; qi < queue.length; qi += 1) {
    const u = queue[qi];
    (outs.get(u) || []).forEach((c) => {
      column.set(c.v, Math.max(column.get(c.v), column.get(u) + c.w));
      indeg.set(c.v, indeg.get(c.v) - 1);
      if (indeg.get(c.v) === 0) queue.push(c.v);
    });
  }

  // Compact: drop columns nothing stands in (blockers stand in host − offset).
  const used = new Set(column.values());
  placedOn.forEach((p) => { if (column.has(p.host)) used.add(column.get(p.host) - p.offset); });
  const remap = new Map([...used].sort((a, b) => a - b).map((c, i) => [c, i + 1]));
  const offsetUsed = (c) => remap.get(c);
  members.forEach((id) => column.set(id, offsetUsed(column.get(id))));
  return { column, broken, remap };
}

/** One cycle among the active constraints, as constraint indices, or null. */
function findCycle(nodes, cons, active) {
  const outs = new Map();
  active.forEach((i) => {
    const c = cons[i];
    if (!outs.has(c.u)) outs.set(c.u, []);
    outs.get(c.u).push(i);
  });
  const state = new Map(); // 1 on stack, 2 done
  const via = new Map();
  for (const start of nodes) {
    if (state.get(start)) continue;
    const stack = [[start, 0]];
    state.set(start, 1);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const [u, k] = top;
      const list = outs.get(u) || [];
      if (k >= list.length) { state.set(u, 2); stack.pop(); continue; }
      top[1] += 1;
      const ci = list[k];
      const v = cons[ci].v;
      if (state.get(v) === 1) {
        const cyc = [ci];
        let w = u;
        while (w !== v) { const back = via.get(w); cyc.push(back); w = cons[back].u; }
        return cyc;
      }
      if (!state.get(v)) { state.set(v, 1); via.set(v, ci); stack.push([v, 0]); }
    }
  }
  return null;
}
